import React, { useState, useEffect } from 'react';

const ROUNDS = Array.from({ length: 25 }, (_, i) => i);

const selectArrowStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 8px center',
  backgroundSize: '14px',
};

const numberInputClass =
  'w-full bg-slate-800 border border-slate-700/50 text-white font-mono tabular-nums rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-emerald-500 disabled:opacity-50 transition-colors';

const secondaryButtonClass =
  'text-[11px] font-mono font-medium text-slate-300 border border-slate-700/70 rounded-lg hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all';

const primaryButtonClass =
  'text-[11px] font-mono font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-all';

const FilterToolsModal = ({
  isOpen,
  onClose,
  onSave,
  onClear,
  selectedFrom,
  selectedTo,
  voteFrequencyEnabled = false,
  freqThresholds = {
    polledMin: 0.7,
    threeMin: 2.4,
    twoMin: 1.4,
    twoMax: 2.4,
    oneMin: 0.7,
    oneMax: 1.4,
  },
}) => {
  // Mode: 'leader' (0..X) or 'range' (from..to)
  const [mode, setMode] = useState('leader');
  const [leaderRound, setLeaderRound] = useState(selectedTo ?? null);
  const [from, setFrom] = useState(selectedFrom ?? null);
  const [to, setTo] = useState(selectedTo ?? null);
  // Vote frequency state
  const [vfEnabled, setVfEnabled] = useState(voteFrequencyEnabled);
  const [vfCustom, setVfCustom] = useState(false);
  const [vf, setVf] = useState({ ...freqThresholds });

  // Keep local state in sync when parent changes (rare)
  useEffect(() => {
    setFrom(selectedFrom ?? null);
    setTo(selectedTo ?? null);
    setLeaderRound(selectedTo ?? null);
    // Infer mode from current selection
    if ((selectedFrom ?? null) === 0 && (selectedTo ?? null) !== null) {
      setMode('leader');
    } else if (selectedFrom !== null || selectedTo !== null) {
      setMode('range');
    } else {
      setMode('leader');
    }
    setVfEnabled(voteFrequencyEnabled);
    setVf({ ...freqThresholds });
  }, [selectedFrom, selectedTo]);

  if (!isOpen) return null;

  const handleSave = () => {
    if (mode === 'leader') {
      if (leaderRound === null || leaderRound === '') {
        onSave && onSave({ from: null, to: null, voteFrequency: { enabled: vfEnabled, thresholds: vf } });
      } else {
        onSave && onSave({ from: 0, to: leaderRound, voteFrequency: { enabled: vfEnabled, thresholds: vf } });
      }
    } else {
      const resolvedFrom = from ?? 0;
      const resolvedTo = to ?? 24;
      if (resolvedFrom === 0 && resolvedTo === 24) {
        onSave && onSave({ from: null, to: null, voteFrequency: { enabled: vfEnabled, thresholds: vf } });
      } else {
        onSave && onSave({ from: resolvedFrom, to: resolvedTo, voteFrequency: { enabled: vfEnabled, thresholds: vf } });
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-2xl border border-slate-700/60 bg-[#0f172a] shadow-2xl shadow-black/60 h-[80vh] sm:h-auto max-h-[88vh] md:max-h-[90vh] overflow-y-auto overflow-x-hidden styled-scrollbar">
        {/* Sticky Header */}
        <div className="sticky top-0 z-20 bg-[#0f172a]/95 backdrop-blur border-b border-slate-700/50">
          <div className="flex items-center gap-2 justify-between px-4 py-3 sm:px-6 bg-slate-800/30">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-1 h-4 bg-emerald-500" />
              <h2 className="text-sm font-semibold text-white tracking-tight">Filters & Tools</h2>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-200 transition-colors"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-4 sm:px-6 pb-4 sm:pb-6">

        {/* Mode selection */}
        <div className="mt-3 sm:mt-4 mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
          <label className="flex items-center gap-2 text-slate-300 text-xs font-mono whitespace-normal break-words cursor-pointer">
            <input
              type="radio"
              className="accent-emerald-500"
              checked={mode === 'leader'}
              onChange={() => setMode('leader')}
            />
            Leader After Round
          </label>
          <label className="flex items-center gap-2 text-slate-300 text-xs font-mono whitespace-normal break-words cursor-pointer">
            <input
              type="radio"
              className="accent-emerald-500"
              checked={mode === 'range'}
              onChange={() => setMode('range')}
            />
            Custom Range
          </label>
        </div>

        {/* Section 1: Round Filtering */}
        <div className={`mb-6 ${mode !== 'leader' ? 'opacity-50' : ''}`}>
          <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 mb-2 whitespace-normal break-words">Leader After Round</label>
          <select
            value={leaderRound === null ? '' : leaderRound}
            onChange={(e) => setLeaderRound(e.target.value === '' ? null : Number(e.target.value))}
            disabled={mode !== 'leader'}
            className="w-full bg-slate-800 border border-slate-700/50 text-white font-mono rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-emerald-500 cursor-pointer appearance-none pr-8 disabled:cursor-default transition-colors"
            style={selectArrowStyle}
          >
            <option value="">All Rounds</option>
            {ROUNDS.map((r) => (
              <option key={r} value={r}>Round {r}</option>
            ))}
          </select>
          <p className="text-slate-500 text-[11px] mt-2">Filters totals up to the selected round (0–X).</p>
        </div>

        {/* Custom Range with single dual-handle slider */}
        <div className={`mb-6 ${mode !== 'range' ? 'opacity-50' : ''}`}>
          <label className="block text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500 mb-2 whitespace-normal break-words">Custom Round Range (Inclusive)</label>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-slate-400 text-[11px] font-mono tabular-nums">
              <span>Start: {from ?? 0}</span>
              <span>End: {to ?? 24}</span>
            </div>
            <div className="dual-range relative py-2 select-none">
              {/* Track */}
              <div className="h-2 bg-slate-800 rounded-full" />
              {/* Selected range fill */}
              <div
                className="absolute top-1/2 -translate-y-1/2 h-2 bg-emerald-500/70 rounded-full"
                style={{
                  left: `${((from ?? 0) / 24) * 100}%`,
                  width: `${(((to ?? 24) - (from ?? 0)) / 24) * 100}%`,
                }}
              />
              {/* Lower thumb */}
              <input
                type="range"
                min={0}
                max={24}
                step={1}
                value={from ?? 0}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  const clamped = Math.min(val, (to ?? 24));
                  setFrom(clamped);
                }}
                disabled={mode !== 'range'}
                aria-label="From round"
              />
              {/* Upper thumb */}
              <input
                type="range"
                min={0}
                max={24}
                step={1}
                value={to ?? 24}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  const clamped = Math.max(val, (from ?? 0));
                  setTo(clamped);
                }}
                disabled={mode !== 'range'}
                aria-label="To round"
              />
            </div>
          </div>
          <style jsx>{`
            .dual-range input[type="range"] {
              -webkit-appearance: none;
              appearance: none;
              position: absolute;
              left: 0;
              right: 0;
              top: 0;
              bottom: 0;
              background: none;
              pointer-events: none; /* allow clicking through to thumbs only */
            }
            .dual-range input[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              pointer-events: auto;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: #10b981; /* emerald-500 */
              border: 2px solid #0f172a; /* slate-900 */
            }
            .dual-range input[type="range"]::-moz-range-thumb {
              pointer-events: auto;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: #10b981;
              border: 2px solid #0f172a;
            }
            .dual-range input[type="range"]::-webkit-slider-runnable-track {
              background: transparent;
            }
            .dual-range input[type="range"]::-moz-range-track {
              background: transparent;
            }
          `}</style>
        </div>

        {/* Divider (mobile only) */}
        <hr className="border-slate-800/70 my-6 sm:hidden" />

        {/* Section 2: Vote Frequency */}
        <div className="mb-4">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-1 h-4 bg-emerald-500" />
            <h3 className="text-sm font-semibold text-white tracking-tight">Vote Frequency</h3>
          </div>
          <label className="flex items-center gap-2 text-slate-300 text-xs font-mono mb-4 cursor-pointer">
            <input
              type="checkbox"
              className="accent-emerald-500"
              checked={vfEnabled}
              onChange={(e) => setVfEnabled(e.target.checked)}
            />
            Show Vote Frequency Columns
          </label>

          <div className={`rounded-xl border border-slate-700/50 bg-slate-900/40 p-4 ${!vfEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">Advanced settings</span>
              <label className="flex items-center gap-2 text-slate-300 text-xs font-mono cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-emerald-500"
                  checked={vfCustom}
                  onChange={(e) => setVfCustom(e.target.checked)}
                />
                Customize thresholds
              </label>
            </div>

            <div className="space-y-4">
              {/* Row: 3 Vote Games - single input */}
              <div>
                <label className="block text-slate-500 text-[11px] font-mono mb-1">3 Vote Games threshold (Start ≥)</label>
                <input
                  type="number"
                  step="0.1"
                  className={numberInputClass}
                  value={vf.threeMin}
                  onChange={(e) => setVf({ ...vf, threeMin: Number(e.target.value) })}
                  disabled={!vfCustom}
                />
              </div>

              {/* Row: 2 Vote Games - two inputs */}
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-500 text-[11px] font-mono mb-1">2 Vote Games threshold (Start ≥)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={numberInputClass}
                      value={vf.twoMin}
                      onChange={(e) => setVf({ ...vf, twoMin: Number(e.target.value) })}
                      disabled={!vfCustom}
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-[11px] font-mono mb-1">2 Vote Games threshold (End &lt;)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={numberInputClass}
                      value={vf.twoMax}
                      onChange={(e) => setVf({ ...vf, twoMax: Number(e.target.value) })}
                      disabled={!vfCustom}
                    />
                  </div>
                </div>
              </div>

              {/* Row: 1 Vote Games - two inputs */}
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-500 text-[11px] font-mono mb-1">1 Vote Games threshold (Start ≥)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={numberInputClass}
                      value={vf.oneMin}
                      onChange={(e) => setVf({ ...vf, oneMin: Number(e.target.value) })}
                      disabled={!vfCustom}
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-[11px] font-mono mb-1">1 Vote Games threshold (End &lt;)</label>
                    <input
                      type="number"
                      step="0.1"
                      className={numberInputClass}
                      value={vf.oneMax}
                      onChange={(e) => setVf({ ...vf, oneMax: Number(e.target.value) })}
                      disabled={!vfCustom}
                    />
                  </div>
                </div>
              </div>

              {/* Row: Games Polled - single input */}
              <div>
                <label className="block text-slate-500 text-[11px] font-mono mb-1">Games Polled threshold (Start ≥)</label>
                <input
                  type="number"
                  step="0.1"
                  className={numberInputClass}
                  value={vf.polledMin}
                  onChange={(e) => setVf({ ...vf, polledMin: Number(e.target.value) })}
                  disabled={!vfCustom}
                />
              </div>
            </div>
            <p className="text-slate-500 text-[11px] font-mono mt-3">Defaults: Polled ≥ 0.7, 3 Votes ≥ 2.4, 2 Votes ≥ 1.4 and &lt; 2.4, 1 Vote ≥ 0.7 and &lt; 1.4</p>
          </div>
        </div>

        {/* Sticky Footer Buttons (mobile-only border; desktop normal) */}
        <div className="sticky bottom-0 z-20 bg-[#0f172a] border-t border-slate-700/50 sm:border-t-0">
          <div className="p-3 sm:hidden grid grid-cols-3 gap-2">
            <button
              onClick={() => onClear && onClear()}
              className={`px-3 py-2 ${secondaryButtonClass}`}
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className={`px-3 py-2 ${secondaryButtonClass}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className={`px-3 py-2 ${primaryButtonClass}`}
            >
              Save
            </button>
          </div>
          <div className="hidden sm:flex items-center justify-end gap-3 p-0 sm:p-0">
            <button
              onClick={() => onClear && onClear()}
              className={`px-4 py-2 ${secondaryButtonClass}`}
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className={`px-4 py-2 ${secondaryButtonClass}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className={`px-4 py-2 ${primaryButtonClass}`}
            >
              Save
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default FilterToolsModal;
